import { useState } from "react";
import type { Distributor, InventoryItem, Product } from "@/types";
import { costAdvisor } from "@/utils/accounting";
import { formatCOP, formatNumber } from "@/utils/format";

interface AdvisorConsignmentBreakdownProps {
  products: Product[];
  inventory: InventoryItem[];
  distributors: Distributor[];
}

interface ConsignedRow {
  product: Product;
  units: number;
  unitCost: number;
  value: number;
}

interface AdvisorSection {
  advisor: Distributor;
  rows: ConsignedRow[];
  units: number;
  value: number;
}

const initialsOf = (d: Distributor): string =>
  `${d.names?.[0] ?? ""}${d.surnames?.[0] ?? ""}`.toUpperCase() || "AS";

// Desglose de la mercancía en consignación que hoy está en manos de la red.
// Cada asesor se abre para ver su detalle por producto y su valor.
const AdvisorConsignmentBreakdown = ({
  products,
  inventory,
  distributors,
}: AdvisorConsignmentBreakdownProps) => {
  const [openId, setOpenId] = useState<string | null>(null);

  const sections: AdvisorSection[] = distributors
    .map((advisor) => {
      const rows: ConsignedRow[] = [];
      products.forEach((product) => {
        const item = inventory.find((i) => i.productId === product.id);
        const units = item?.stockByAdvisor[advisor.id] ?? 0;
        if (units <= 0) return;
        const unitCost = costAdvisor(product.pricePublic, advisor.margin);
        rows.push({ product, units, unitCost, value: units * unitCost });
      });
      rows.sort((a, b) => b.value - a.value);
      const units = rows.reduce((a, r) => a + r.units, 0);
      const value = rows.reduce((a, r) => a + r.value, 0);
      return { advisor, rows, units, value };
    })
    .filter((s) => s.units > 0);

  return (
    <div className="mt-5">
      <div className="flex items-center gap-2 mb-3">
        <span className="w-8 h-8 rounded-md bg-secondary-500/20 text-secondary-200 flex items-center justify-center">
          <i className="ri-team-line" />
        </span>
        <div>
          <h3 className="editable-title text-base font-bold">Consignación por asesor</h3>
          <p className="text-[11px] text-foreground-600">
            Mercancía que hoy está en manos de la red. Abre cada asesor para ver el detalle por producto.
          </p>
        </div>
      </div>

      {sections.length === 0 ? (
        <div className="glass-panel rounded-lg px-5 py-8 text-center">
          <p className="text-sm text-foreground-700 font-semibold">
            Nadie tiene mercancía en consignación
          </p>
          <p className="text-xs text-foreground-500 mt-1">
            Cuando entregues mercancía a un asesor aparecerá aquí el desglose por producto.
          </p>
        </div>
      ) : (
        <div className="glass-panel rounded-lg overflow-hidden divide-y divide-background-300/25">
          {sections.map((s) => {
            const isOpen = openId === s.advisor.id;
            return (
              <div key={s.advisor.id}>
                <button
                  onClick={() => setOpenId(isOpen ? null : s.advisor.id)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left cursor-pointer hover:bg-background-200/25 transition-colors"
                >
                  <span className="w-9 h-9 rounded-full bg-accent-500/15 text-accent-200 flex items-center justify-center text-[11px] font-bold shrink-0">
                    {initialsOf(s.advisor)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-foreground-950 truncate">
                      {s.advisor.names} {s.advisor.surnames}
                    </p>
                    <p className="text-[11px] text-foreground-500">
                      {s.rows.length} producto{s.rows.length === 1 ? "" : "s"} en consignación ·{" "}
                      {formatNumber(s.units)} und
                    </p>
                  </div>
                  <span className="text-sm font-bold text-accent-200 whitespace-nowrap">
                    {formatCOP(s.value)}
                  </span>
                  <span className="w-6 h-6 flex items-center justify-center text-foreground-500">
                    <i
                      className={`ri-arrow-down-s-line transition-transform ${
                        isOpen ? "rotate-180" : ""
                      }`}
                    />
                  </span>
                </button>

                {isOpen && (
                  <div className="px-4 pb-4 pt-1 bg-background-200/20">
                    <div className="rounded-md border border-background-300/40 overflow-hidden overflow-x-auto">
                      <table className="w-full min-w-[420px] text-left">
                        <thead>
                          <tr className="bg-background-200/50 text-[10px] uppercase tracking-wide text-foreground-500">
                            <th className="px-3 py-2 font-semibold">Producto</th>
                            <th className="px-3 py-2 font-semibold text-right">Und.</th>
                            <th className="px-3 py-2 font-semibold text-right whitespace-nowrap">
                              Costo asesor
                            </th>
                            <th className="px-3 py-2 font-semibold text-right">Valor</th>
                          </tr>
                        </thead>
                        <tbody className="text-xs">
                          {s.rows.map((r) => (
                            <tr
                              key={r.product.id || r.product.sku}
                              className="border-t border-background-300/30"
                            >
                              <td className="px-3 py-2">
                                <span className="block text-foreground-900 font-medium truncate max-w-[220px]">
                                  {r.product.name}
                                </span>
                                <span className="block font-mono text-[10px] text-accent-200">
                                  {r.product.sku}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-right text-foreground-800 whitespace-nowrap">
                                {formatNumber(r.units)}
                              </td>
                              <td className="px-3 py-2 text-right text-foreground-700 whitespace-nowrap">
                                {formatCOP(r.unitCost)}
                              </td>
                              <td className="px-3 py-2 text-right font-semibold text-foreground-950 whitespace-nowrap">
                                {formatCOP(r.value)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="border-t border-accent-500/25 bg-background-200/50">
                            <td
                              className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-foreground-600"
                              colSpan={3}
                            >
                              Total en consignación · margen {s.advisor.margin}%
                            </td>
                            <td className="px-3 py-2 text-right font-bold text-accent-200 whitespace-nowrap">
                              {formatCOP(s.value)}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                    <p className="text-[10px] text-foreground-500 mt-2">
                      Valor a costo asesor (precio público menos el margen del {s.advisor.margin}%). Es lo
                      que el asesor liquida cuando vende.
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdvisorConsignmentBreakdown;